import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api.ts";
import { AvisoError, CampoClave, useAccion } from "../pantallas/comun.tsx";

interface Estado { vinculada: boolean; activo: boolean; obligatorio: boolean; rescateRestantes: number; equiposDeConfianza: number; diasRestantes: number | null }
interface Preparada { secreto: string; uri: string; qr: string[] }
type Fase = "inicio" | "codigo" | "rescate" | "apagar" | "nuevos";

/** El código QR como dibujo (siempre negro sobre blanco, con su margen, para que cualquier app lo lea aunque la pantalla esté en modo oscuro). */
function Qr({ filas }: { filas: string[] }) {
  const n = filas.length, m = 4;
  const trazo = filas.map((f, y) => [...f].map((c, x) => (c === "1" ? `M${x + m} ${y + m}h1v1h-1z` : "")).join("")).join("");
  return (
    <svg viewBox={`0 0 ${n + 2 * m} ${n + 2 * m}`} width="200" height="200" role="img" aria-label="Código QR para la app de verificación" style={{ background: "#fff", borderRadius: 8, maxWidth: "100%" }} shapeRendering="crispEdges">
      <rect width="100%" height="100%" fill="#fff" /><path d={trazo} fill="#000" />
    </svg>
  );
}

function CampoCodigo({ valor, alCambiar, etiqueta = "Código de 6 dígitos de tu app" }: { valor: string; alCambiar: (v: string) => void; etiqueta?: string }) {
  return (
    <label className="campo">{etiqueta}
      <input className="entrada mono" inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="123 456" required value={valor} onChange={(e) => alCambiar(e.target.value.replace(/[^\d ]/g, ""))} style={{ maxWidth: 160, letterSpacing: 2 }} />
    </label>
  );
}

/**
 * Verificación en dos pasos de la propia cuenta: se activa con una app de verificación (Google Authenticator, Microsoft Authenticator, Authy…),
 * pide un número de 6 dígitos al entrar y deja 8 códigos de rescate por si se pierde el celular.
 * Es obligatoria para administración y dirección; para el resto es opcional.
 */
export function DosPasos({ alCambiar }: { alCambiar?: () => void }) {
  const [e, setE] = useState<Estado | null>(null);
  const [fase, setFase] = useState<Fase>("inicio");
  const [clave, setClave] = useState(""), [codigo, setCodigo] = useState("");
  const [prep, setPrep] = useState<Preparada | null>(null);
  const [rescate, setRescate] = useState<string[] | null>(null);
  const [aviso, setAviso] = useState("");
  const a = useAccion();
  const cargar = () => api<Estado>("GET", "/api/mi-seguridad").then(setE).catch(() => undefined);
  useEffect(() => { cargar(); }, []);
  const termino = () => { setClave(""); setCodigo(""); setPrep(null); cargar(); alCambiar?.(); window.dispatchEvent(new Event("donna:yo")); };

  if (!e) return <p className="muted">Cargando…</p>;
  if (e.vinculada) return <p className="muted" style={{ margin: 0 }}>Esta cuenta entra desde tu cuenta principal (en tu otra clínica): la verificación en dos pasos se configura allí.</p>;

  /** Un solo clic: genera el QR de una vez; la contraseña se pide al confirmar con el código. */
  async function preparar() {
    a.setError(null);
    const r = await a.correr(() => api<Preparada>("POST", "/api/mi-seguridad/2fa/preparar", {}));
    if (r) { setPrep(r); setFase("codigo"); setClave(""); setCodigo(""); }
  }
  async function activar(ev: FormEvent) {
    ev.preventDefault();
    const r = await a.correr(() => api<{ rescate: string[] }>("POST", "/api/mi-seguridad/2fa/activar", { contrasena: clave, codigo }));
    if (r) { setRescate(r.rescate); setFase("rescate"); setClave(""); setCodigo(""); setPrep(null); cargar(); window.dispatchEvent(new Event("donna:yo")); }
  }
  async function apagar(ev: FormEvent) {
    ev.preventDefault();
    const r = await a.correr(() => api<unknown>("POST", "/api/mi-seguridad/2fa/desactivar", { contrasena: clave, codigo }));
    if (r !== undefined) { setAviso("Desactivaste la verificación en dos pasos."); setFase("inicio"); termino(); }
  }
  async function nuevos(ev: FormEvent) {
    ev.preventDefault();
    const r = await a.correr(() => api<{ rescate: string[] }>("POST", "/api/mi-seguridad/2fa/rescate-nuevos", { contrasena: clave, codigo }));
    if (r) { setRescate(r.rescate); setFase("rescate"); setClave(""); setCodigo(""); cargar(); }
  }
  async function olvidar() {
    if (!confirm("¿Olvidar los equipos de confianza? Donna volverá a pedir el código en cada ingreso, también en este equipo.")) return;
    const r = await a.correr(() => api<{ olvidados: number }>("POST", "/api/mi-seguridad/equipos/olvidar"));
    if (r) { setAviso(`Listo: ${r.olvidados} equipo${r.olvidados === 1 ? "" : "s"} olvidado${r.olvidados === 1 ? "" : "s"}.`); cargar(); }
  }
  const cancelar = () => { setFase("inicio"); setClave(""); setCodigo(""); setPrep(null); a.setError(null); };
  const descargar = () => {
    const blob = new Blob([`Códigos de rescate de Donna\nCada código sirve una sola vez. Guárdalos en un lugar seguro.\n\n${rescate!.join("\n")}\n`], { type: "text/plain" });
    const enlace = document.createElement("a"); enlace.href = URL.createObjectURL(blob); enlace.download = "codigos-de-rescate-donna.txt"; enlace.click(); setTimeout(() => URL.revokeObjectURL(enlace.href), 2000);
  };

  if (fase === "rescate" && rescate) return (
    <div style={{ display: "grid", gap: 10 }}>
      <div className="aviso ok" role="status">¡Listo! La verificación en dos pasos está activa.</div>
      <h3 style={{ margin: 0 }}>Guarda tus códigos de rescate</h3>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Si pierdes tu celular, cada uno de estos códigos te deja entrar <b>una vez</b>. Donna no los vuelve a mostrar. Guárdalos en un lugar seguro, <b>fuera de este computador</b> (impresos o en un administrador de contraseñas).</p>
      <ul className="mono" style={{ columns: 2, margin: 0, padding: "10px 24px", background: "var(--surface-2, rgba(127,127,127,.12))", borderRadius: 8, fontSize: 16, lineHeight: 1.9 }}>{rescate.map((c) => <li key={c}>{c}</li>)}</ul>
      <div className="fila" style={{ flexWrap: "wrap" }}>
        <button className="btn" onClick={() => navigator.clipboard?.writeText(rescate.join("\n")).then(() => setAviso("Códigos copiados."))}>Copiar</button>
        <button className="btn" onClick={descargar}>Descargar</button>
        <button className="btn pri" onClick={() => { setRescate(null); setFase("inicio"); termino(); }}>Ya los guardé</button>
      </div>
      {aviso && <small className="muted" role="status">{aviso}</small>}
    </div>
  );

  if (fase === "codigo" && prep) return (
    <form onSubmit={activar} style={{ display: "grid", gap: 12 }}>
      <ol style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6 }}>
        <li>Instala en tu celular una app de verificación: <b>Google Authenticator</b>, <b>Microsoft Authenticator</b> o <b>Authy</b> (gratis).</li>
        <li>En la app toca «+» y escanea este código. Si no puedes escanear, escribe esta clave a mano.</li>
        <li>Escribe aquí tu contraseña y el número de 6 dígitos que muestra la app.</li>
      </ol>
      <div className="fila" style={{ flexWrap: "wrap", alignItems: "center", gap: 16 }}>
        <Qr filas={prep.qr} />
        <div><small className="muted">Clave para escribir a mano</small><div className="mono" style={{ fontSize: 15, letterSpacing: 1, userSelect: "all" }}>{prep.secreto}</div></div>
      </div>
      <CampoClave etiqueta="Tu contraseña (para confirmar)" required value={clave} onChange={setClave} />
      <CampoCodigo valor={codigo} alCambiar={setCodigo} />
      <AvisoError error={a.error} />
      <div className="fila"><button className="btn pri" disabled={a.ocupado || !clave || codigo.replace(/\s/g, "").length !== 6}>Activar</button><button type="button" className="btn" onClick={cancelar}>Cancelar</button></div>
    </form>
  );

  if (fase === "apagar" || fase === "nuevos") return (
    <form onSubmit={fase === "apagar" ? apagar : nuevos} style={{ display: "grid", gap: 10, maxWidth: 420 }}>
      <p className="muted" style={{ margin: 0 }}>{fase === "apagar" ? "Para desactivarla escribe tu contraseña y un código de tu app." : "Los códigos anteriores dejarán de servir. Escribe tu contraseña y un código de tu app."}</p>
      <CampoClave etiqueta="Tu contraseña" required value={clave} onChange={setClave} />
      <CampoCodigo valor={codigo} alCambiar={setCodigo} />
      <AvisoError error={a.error} />
      <div className="fila"><button className="btn pri" disabled={a.ocupado || !clave || codigo.replace(/\s/g, "").length !== 6}>{fase === "apagar" ? "Desactivar" : "Generar códigos nuevos"}</button><button type="button" className="btn" onClick={cancelar}>Cancelar</button></div>
    </form>
  );

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div className="fila" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span className={`chip${e.activo ? "" : " rojo"}`}>{e.activo ? "Activa" : "No activada"}</span>
        {!e.activo && e.obligatorio && <span className="chip rojo">{e.diasRestantes ? `Obligatoria para tu rol · te quedan ${e.diasRestantes} día${e.diasRestantes === 1 ? "" : "s"}` : "Obligatoria para tu rol · debes activarla para seguir"}</span>}
      </div>
      {aviso && <div className="aviso ok" role="status">{aviso}</div>}
      {e.activo ? (
        <>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Al entrar, Donna te pide el número de 6 dígitos de tu app. Te quedan <b>{e.rescateRestantes}</b> código{e.rescateRestantes === 1 ? "" : "s"} de rescate{e.equiposDeConfianza ? ` y ${e.equiposDeConfianza} equipo${e.equiposDeConfianza === 1 ? "" : "s"} de confianza` : ""}.</p>
          <div className="fila" style={{ flexWrap: "wrap" }}>
            <button className="btn" onClick={() => { a.setError(null); setFase("nuevos"); }}>Códigos de rescate nuevos</button>
            {e.equiposDeConfianza > 0 && <button className="btn" onClick={olvidar}>Olvidar equipos de confianza</button>}
            {!e.obligatorio && <button className="btn" onClick={() => { a.setError(null); setFase("apagar"); }}>Desactivar</button>}
          </div>
        </>
      ) : (
        <>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Protege tu cuenta aunque alguien se entere de tu contraseña: además de ella, Donna pide un número de 6 dígitos que solo muestra tu celular. Toma dos minutos.</p>
          <div><button className="btn pri" disabled={a.ocupado} onClick={preparar}>Activar la verificación en dos pasos</button></div>
        </>
      )}
      <AvisoError error={a.error} />
    </div>
  );
}

/** Pantalla que bloquea el uso de Donna a una cuenta de administración o dirección cuando ya pasó su plazo para activarla. */
export function ActivarDosPasos({ nombre, alSalir, alTerminar }: { nombre: string; alSalir: () => void; alTerminar: () => void }) {
  return (
    <main className="login">
      <div className="card" style={{ display: "grid", gap: 12, maxWidth: 560 }}>
        <h1 style={{ fontSize: 22, margin: 0 }}>Activa la verificación en dos pasos</h1>
        <p className="muted" style={{ margin: 0 }}>Hola, {nombre}. Las cuentas de administración y dirección ven toda la información de la clínica, por eso Donna exige este paso extra de seguridad. Ya pasó el plazo para activarla: hazlo ahora para seguir trabajando.</p>
        <DosPasos alCambiar={alTerminar} />
        <button className="btn" onClick={alSalir}>Salir</button>
      </div>
    </main>
  );
}
